import { QuickStartTask } from "common/constants";
import { ActionPermissionsObject, PermissionActionType, WorkspaceResourceType, WorkspacePlanTier } from "common/interfaces";
import { getCanvasBundle } from "js/canvas";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { appVersion } from "js/config";
import QuickStartController from "js/controllers/QuickStartController";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import { Key } from "js/core/utilities/keys";
import { trackActivity } from "js/core/utilities/utilities";
import getLogger, { LogGroup } from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { shouldUseUpgradeIconForAiEntry } from "js/editor/AiGeneration/aiNavigationIcon";
import TaskRendererFactory from "js/editor/PresentationEditor/Components/QuickStart/TaskRendererFactory";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { ShowErrorDialog, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import {
    Divider,
    UIContainer,
    UINavigation,
    UINavigationItem
} from "js/react/components/UiComponents";
import { AddSlideController } from "js/react/views/AddSlide/AddSlideController";
import SearchSlidesPane from "js/react/views/AddSlide/Panes/SearchSlidesPane";
import { TeamResourceTypes } from "js/react/views/TeamResources/TeamResources";
import { $, _ } from "js/vendor";
import React, { Component } from "react";
import Analytics from "../../../analytics";
import GenerateNewSlidePane from "../../../editor/AiGeneration/SlideAi/GenerateNewSlidePane";
import { disposeAllIndexes } from "./DataServices";
import { CopySlidePane } from "./Panes/CopySlidePane";
import { ImportSlidesFromPPTPane } from "./Panes/ImportSlidesFromPPTPane";
import SlideGalleryPane from "./Panes/SlideGalleryPane";

import "css/add-slide.scss";
import { t } from "js/i18n";
import { AiIconCircle } from "../../../editor/AiGeneration/AiIcon";
import SharedSlidesPane from "../SharedResources/SharedSlidesPane";

const logger = getLogger(LogGroup.TEAMS);

class AddSlideContainer extends Component {
    state = {
        selectedPaneId: "search",
        showUserTheme: false,
        quickStartPopoverState: null,
        selectedTeamSlideType: [WorkspaceResourceType.SLIDE, WorkspaceResourceType.SLIDE_TEMPLATE]
    };

    constructor() {
        super();

        this.searchSlidesPaneRef = React.createRef();
    }

    componentDidMount() {
        PresentationEditorController.setPopupState(true);
        $(window).on("keydown.add-slide-container", event => {
            if (event.which == Key.ESCAPE) {
                this.handleBackButton();
            }
        });

        // Check if QuickStart task is not done and show popover
        this.checkAndShowQuickStartPopover();
    }

    checkAndShowQuickStartPopover = () => {
        if (!QuickStartController.isInitialized) {
            return;
        }

        // Check if the task is not done
        const isTaskDone = QuickStartController.getTaskStatus(QuickStartTask.ADD_YOUR_FIRST_SMART_SLIDE);

        if (!isTaskDone) {
            // Wait for the component to fully render before showing popover
            setTimeout(() => {
                // Position in bottom left corner of viewport
                const bottomOffset = 24; // 24px from bottom
                const leftOffset = 24; // 24px from left
                const position = {
                    top: window.innerHeight - bottomOffset,
                    left: leftOffset
                };

                this.setState({
                    quickStartPopoverState: {
                        position,
                        transformOrigin: { vertical: "bottom", horizontal: "left" }
                    }
                });
            }, 500);
        }
    }

    scrollToCompareRadialBarChart = () => {
        // Find the element with data-id="compare_radial_bars"
        const element = document.querySelector('[data-id="compare_radial_bars"]');
        if (element) {
            // Scroll the element into view
            element.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "nearest"
            });
        }
    }

    handleCloseQuickStartPopover = () => {
        this.setState({ quickStartPopoverState: null });
    }

    componentWillUnmount() {
        PresentationEditorController.setPopupState(false);
        $(window).off("keydown.add-slide-container");

        disposeAllIndexes();
    }

    handleSelectPane = (paneId, resourceTypes = null) => {
        if (paneId == "team-slides" && resourceTypes) {
            this.setState({
                selectedTeamSlideType: resourceTypes,
                selectedPaneId: paneId
            });
            return;
        }

        if (paneId == "search" && this.state.selectedPaneId == "search") {
            if (this.searchSlidesPaneRef.current.state.query != "") {
                this.searchSlidesPaneRef.current.handleClearSearch();
                return;
            }
            if (this.searchSlidesPaneRef.current.state.selectedTemplate != null) {
                this.searchSlidesPaneRef.current.setState({ selectedTemplate: null });
                return;
            }
        }

        this.setState({ selectedPaneId: paneId });
    }

    handleBackButton = () => {
        const { closeDialog } = this.props;

        if (this.state.selectedPresentationId) {
            this.setState({
                selectedPresentationId: null
            });
            return;
        }

        if (this.state.selectedPaneId == "search" && this.searchSlidesPaneRef.current.state.query != "") {
            this.searchSlidesPaneRef.current.handleClearSearch();
            return;
        }
        if (this.state.selectedPaneId == "search" && this.searchSlidesPaneRef.current.state.selectedTemplate != null) {
            this.searchSlidesPaneRef.current.setState({
                selectedTemplate: null
            });
            return;
        }

        closeDialog(false);
    }

    handleAddSlideFromTemplate = async template => {
        if (this.isClosing) return;
        this.isClosing = true;

        await AddSlideController.addSlideFromTemplate(template, this.props.replaceSlide);
        _.defer(() => {
            this.props.closeDialog(true);
        });
    }

    handleToggleUserTheme = () => {
        this.setState({ showUserTheme: !this.state.showUserTheme });
    }

    handleAddBlankClassicSlide = async () => {
        const { slideTemplates: { classicTemplates } } = await getCanvasBundle(window.FORCE_NEW_SLIDES_VERSION ?? appVersion);
        const blank = classicTemplates.find(template => template.id === "authoring");
        return this.handleAddSlideFromClassicTemplate(blank);
    }

    handleAddSlideLayout = ({ slide }) => {
        if (this.isClosing) return;
        this.isClosing = true;

        AddSlideController.addSlideFromModel({
            // We'll copy the entire model, but the slide will be referencing the original team slide,
            // if the team slide is deleted, then we'll just fall back to the baked in local model,
            // this way no data will ever be lost
            ..._.cloneDeep(_.omit(slide.attributes, ["id", "_changeId", "libraryItemId", "presentationId"])),
            themeSlideId: slide.id
        });

        trackActivity("SlideLayout", "Selected", slide.id);

        _.defer(() => {
            this.props.closeDialog(true);
        });
    }

    handleAddSlideFromClassicTemplate = async template => {
        if (this.isClosing) return;
        this.isClosing = true;

        const slideModel = {
            template_id: "authoring",
            layout: {
                headerPosition: template.headerPosition ?? "none",
                showFooter: "off"
            },
            states: [{
                primary: template.model,
                header: template.headerModel
            }],
            version: template.version,
            migrationVersion: template.migrationVersion
        };

        AddSlideController.addSlideFromModel(slideModel);

        trackActivity("ClassicTemplate", "Selected", template.id);

        _.defer(() => {
            this.props.closeDialog(true);
        });
    }

    handleCopySlidesFromInspirationGallery = async slide => {
        if (this.isClosing) return;
        this.isClosing = true;

        try {
            await AddSlideController.copySlidesIntoPresentation([slide], this.props.replaceSlide, "gallery");
            this.props.closeDialog(true);
        } catch (err) {
            ShowErrorDialog({
                title: t("Unable to copy slide into presentation"),
                message: err.message
            });
            this.isClosing = false;
        }
    }

    handleCopySlidesFromPresentation = async slides => {
        if (this.isClosing) return;
        this.isClosing = true;

        try {
            await AddSlideController.copySlidesIntoPresentation(slides, this.props.replaceSlide, "presentation");
            this.props.closeDialog(true);
        } catch (err) {
            ShowErrorDialog({
                title: t("Unable to copy slide into presentation"),
                message: err.message
            });
            this.isClosing = false;
        }
    }

    handleCopySlideFromLibrary = async teamSlidesDataServiceDataItems => {
        if (this.isClosing) return;
        this.isClosing = true;

        let failedSlideNames = [];
        try {
            await withProgressDialog({
                title: t("Adding selected slides to presentation"),
                action: async () => {
                    await Promise.all(teamSlidesDataServiceDataItems.map(async teamSlidesDataServiceDataItem => {
                        await AddSlideController.copySlideFromLibraryIntoPresentation(teamSlidesDataServiceDataItem.id, teamSlidesDataServiceDataItem.libraryItemId)
                            .catch(err => {
                                if (err.message.includes("Slide has already been added")) {
                                    failedSlideNames.push(teamSlidesDataServiceDataItem.name);
                                }
                            });
                    }));
                }
            });
            if (failedSlideNames.length > 0) {
                throw new Error(`${t("The following slides have already been added to this presentation")}: ${failedSlideNames.join(", ")}`);
            }
            this.props.closeDialog(true);
        } catch {
            // if there were any slides added successfully, we should still send them to the editor
            if (failedSlideNames.length < teamSlidesDataServiceDataItems.length) {
                ShowMessageDialog({
                    title: t("We've added only the Shared Slides that weren't already in your presentation."),
                    message: <>
                        {t("Shared slides can't be duplicated within the same presentation. The following slides were not added")}:<br />
                        <ul>
                            {failedSlideNames.map(name => <li key={name}>{name}</li>)}
                        </ul>
                    </>,
                    dialogContentProps: {
                        style: { paddingBottom: 0 }
                    }
                });
                this.props.closeDialog(true);
            } else {
                ShowErrorDialog({
                    title: t("Oops! We couldn’t add the following Shared Slides to your presentation."),
                    message: <>
                        {t("These slides have already been added to your presentation. Shared Slides cannot be added more than once.")}<br />
                        <ul>
                            {failedSlideNames.map(name => <li key={name}>{name}</li>)}
                        </ul>
                    </>
                });
                this.isClosing = false;
            }
        }
    }

    handleAddSlideFromTeamSlideTemplate = async teamSlideTemplates => {
        const { closeDialog } = this.props;
        if (this.isClosing) return;
        this.isClosing = true;

        try {
            await withProgressDialog({
                title: t("Adding selected slides to presentation"),
                action: async () => {
                    for (const teamSlideTemplate of teamSlideTemplates) {
                        await AddSlideController.addSlideFromTeamSlideTemplate(teamSlideTemplate);
                    }
                }
            });
            closeDialog(true);
        } catch (err) {
            logger.error(err, "[AddSlideContainer] handleAddSlideFromTeamSlideTemplate failed");

            ShowErrorDialog({
                title: t("Unable to add slides into presentation"),
                message: err.message
            });
            this.isClosing = false;
        }
    }

    handleAddSlideFromSharedSlides = async sharedSlides => {
        const { closeDialog } = this.props;

        if (this.isClosing) return;

        if (!WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use) {
            return;
        }

        const teamSlideTemplates = sharedSlides.filter(slide => slide.isTemplate);
        const teamSlides = sharedSlides.filter(slide => !slide.isTemplate);

        if (teamSlideTemplates.length === 0 && teamSlides.length === 0) {
            return;
        }

        this.isClosing = true;

        let failedSlideNames = [];
        try {
            await withProgressDialog({
                title: t("Adding selected slides to presentation"),
                action: async () => {
                    for (const teamSlideTemplate of teamSlideTemplates) {
                        await AddSlideController.addSlideFromTeamSlideTemplate(teamSlideTemplate);
                    }
                    await Promise.all(teamSlides.map(async teamSlide => {
                        await AddSlideController.copySlideFromLibraryIntoPresentation(teamSlide.id, teamSlide.libraryItemId)
                            .catch(err => {
                                if (err.message.includes("Slide has already been added")) {
                                    failedSlideNames.push(teamSlide.name);
                                }
                            });
                    }));
                }
            });
            if (failedSlideNames.length > 0) {
                throw new Error(`${t("The following slides have already been added to this presentation")}: ${failedSlideNames.join(", ")}`);
            }
            closeDialog(true);
        } catch {
            const hadPartialSuccess = failedSlideNames.length < teamSlides.length || teamSlideTemplates.length > 0;
            if (hadPartialSuccess) {
                ShowMessageDialog({
                    title: t("We've added only the Shared Slides that weren't already in your presentation."),
                    message: <>
                        {t("Shared slides can't be duplicated within the same presentation. The following slides were not added")}:<br />
                        <ul>
                            {failedSlideNames.map(name => <li key={name}>{name}</li>)}
                        </ul>
                    </>,
                    dialogContentProps: {
                        style: { paddingBottom: 0 }
                    }
                });
                closeDialog(true);
            } else {
                ShowErrorDialog({
                    title: t("Oops! We couldn’t add the following Shared Slides to your presentation."),
                    message: <>
                        {t("These slides have already been added to your presentation. Shared Slides cannot be added more than once.")}<br />
                        <ul>
                            {failedSlideNames.map(name => <li key={name}>{name}</li>)}
                        </ul>
                    </>
                });
                this.isClosing = false;
            }
        }
    }

    handlePPTImportFinished = () => {
        if (this.isClosing) return;
        this.isClosing = true;
        if (PresentationEditorController.currentSlide) {
            PresentationEditorController.goNextSlide();
        }
        this.props.closeDialog(true);
    }

    handleGeneratedSlide = async (slideModel, prompt, aiRequest) => {
        if (this.isClosing) return;
        this.isClosing = true;

        await withProgressDialog({
            title: t("Adding slide"),
            action: async () => {
                const slides = await AddSlideController.addSlideFromModel(slideModel);

                Analytics.AiGeneration.GenerateSlide({ slide: slides[0] });

                _.defer(() => {
                    this.props.closeDialog(true);
                });
            }
        });
    }

    renderSelectedPane = () => {
        const { forTeamSlide = false, currentTheme } = this.props;
        const { selectedPaneId, selectedTeamSlideType } = this.state;

        const workspaceId = AppController.workspaceId;

        trackActivity("AddSlide", "PaneSelected", null, null, { type: selectedPaneId }, { audit: false, skipAmplitude: true });

        switch (selectedPaneId) {
            case "slide-gallery":
                return (
                    <SlideGalleryPane
                        onSelected={this.handleCopySlidesFromInspirationGallery}
                        onAddBlankClassicSlide={this.handleAddBlankClassicSlide}
                        showUserTheme={this.state.showUserTheme}
                        workspaceId={workspaceId}
                        searchType="SlideGalleryPane"
                        currentTheme={currentTheme}
                    />
                );
            case "copy-slide":
                return (
                    <CopySlidePane
                        forTeamSlide={forTeamSlide}
                        onSelected={this.handleCopySlidesFromPresentation}
                        onAddBlankClassicSlide={this.handleAddBlankClassicSlide}
                        showUserTheme={this.state.showUserTheme}
                        workspaceId={workspaceId}
                    />
                );
            case "team-slides":
                return (
                    <SharedSlidesPane
                        resourceTypes={selectedTeamSlideType}
                        workspaceId={workspaceId}
                        onSelected={this.handleAddSlideFromSharedSlides}
                        showUpvote
                    />
                );
            case "ppt-import":
                return (
                    <ImportSlidesFromPPTPane
                        forTeamSlide={forTeamSlide}
                        onImportFinished={this.handlePPTImportFinished}
                    />
                );
            case "ai-generate":
                return (
                    <GenerateNewSlidePane onGeneratedSlide={this.handleGeneratedSlide} forTeamSlide={forTeamSlide} />
                );
            case "search":
            default:
                return (
                    <SearchSlidesPane
                        forTeamSlide={forTeamSlide}
                        ref={this.searchSlidesPaneRef}
                        workspaceId={workspaceId}
                        onTemplateSelected={this.handleAddSlideFromTemplate}
                        onSlideLayoutSelected={this.handleAddSlideLayout}
                        onClassicTemplateSelected={this.handleAddSlideFromClassicTemplate}
                        onGallerySlideSelected={this.handleCopySlidesFromInspirationGallery}
                        onCopySlideSelected={this.handleCopySlidesFromPresentation}
                        onTeamSlideSelected={this.handleCopySlideFromLibrary}
                        onTeamSlideTemplateSelected={this.handleAddSlideFromTeamSlideTemplate}
                        onSelectViewAll={this.handleSelectPane}
                        onAddBlankClassicSlide={this.handleAddBlankClassicSlide}
                        onToggleUserTheme={this.handleToggleUserTheme}
                        // To show always thumbnails with the current theme
                        showUserTheme={true}
                        hideUseCurrentThemeSwitch={this.props.hideUseCurrentThemeSwitch}
                        currentTheme={currentTheme}
                    />
                );
        }
    }

    renderQuickStartPopover = () => {
        const { quickStartPopoverState } = this.state;

        if (!quickStartPopoverState) {
            return null;
        }

        return (
            <TaskRendererFactory
                type="popover"
                saveOnOpen={false}
                taskKey={QuickStartTask.ADD_YOUR_FIRST_SMART_SLIDE}
                popoverState={quickStartPopoverState}
                onPopoverClose={this.handleCloseQuickStartPopover}
                onCallback={this.scrollToCompareRadialBarChart}
            />
        );
    }

    render() {
        const { closeDialog, hidePPTImport = false, forceShowTeamSlides = false } = this.props;
        const { selectedPaneId } = this.state;
        const workspaceId = AppController.workspaceId;

        const showTeamSlidesPane = (forceShowTeamSlides || (!PresentationEditorController.isSingleSlideEditor));

        const isBasic = WorkspaceController.plan.tier === WorkspacePlanTier.BASIC;

        const designerBotAvailable = WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use;
        const designerBotVisible = designerBotAvailable || WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.GENERATIVE_AI, PermissionActionType.USE);
        const pptImportHidden = hidePPTImport || !WorkspaceController.actionPermissions[ActionPermissionsObject.IMPORT_PPT].use;
        const canUpgradeToUseTeamSlides = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.USE);

        return (
            <UIContainer isOpen={true} className="add-slide-container" onClose={closeDialog}
                         direction="horizontal">
                <UINavigation
                    selected={selectedPaneId}
                    onSelect={id => this.handleSelectPane(id)}
                    onBack={this.handleBackButton}
                >
                    <UINavigationItem
                        key="search"
                        id="search"
                        title={t("Search For A Slide")}
                        icon="search"
                    />
                    {designerBotVisible &&
                        <Divider margin={10} color="#555" />
                    }
                    {designerBotVisible &&
                        <UINavigationItem
                            key="ai"
                            id="ai-generate"
                            title={t("Create with AI...")}
                            image={shouldUseUpgradeIconForAiEntry({
                                isBasic,
                                canUpgradeToUseGenerativeAi: WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.GENERATIVE_AI, PermissionActionType.USE),
                            }) ? <UpgradeIcon style={{ width: 24, height: 24, margin: 0 }} /> : <AiIconCircle />}
                        />
                    }
                    {
                        WorkspaceController.actionPermissions[ActionPermissionsObject.INSPIRATION_SLIDES].use &&
                        <UINavigationItem
                            key="slide-gallery"
                            id="slide-gallery"
                            title={t("Get Inspiration")}
                            icon="lightbulb"
                        />
                    }
                    {
                        // Adding components in an array in order to avoid wrapping it with an empty fragment or have three conditions
                        showTeamSlidesPane && ([
                            <Divider color="#555" margin={10} key="divider" />,
                            <UINavigationItem
                                key="team-slides"
                                id="team-slides"
                                title={t("Team Slides")}
                                icon={!canUpgradeToUseTeamSlides && "library_add"}
                                image={canUpgradeToUseTeamSlides && <UpgradeIcon style={{ width: 24, height: 24, margin: 0 }} />}
                            />,
                        ].map(item => item))
                    }
                    {<Divider color="#555" margin={10} />}
                    {
                        <UINavigationItem
                            key="copy-slide"
                            id="copy-slide"
                            title={t("My Slides")}
                            icon="file_copy"
                        />
                    }
                    {!pptImportHidden && <Divider color="#555" margin={10} />}
                    {!pptImportHidden &&
                        <UINavigationItem
                            key="ppt-import"
                            id="ppt-import"
                            title={t("Import Slides From PPT")}
                            icon="upload"
                        />
                    }
                </UINavigation>
                {this.renderSelectedPane()}
                {this.renderQuickStartPopover()}
            </UIContainer>
        );
    }
}

export default WorkspaceController.withInitializedState(AppController.withState(PresentationEditorController.withState(AddSlideContainer)));
